import React from 'react';
import { Box, Button } from '@mui/material';
import AuditDynamicFormRHFNew from './AuditDynamicFormRHFNew';
import SimpleAuditForm from './SimpleAuditForm';
import type { AuditVarianceByGameId } from '../../types/auditVarianceTypes';
import VendingSalesDynamicFormRHFNew from './VendingSalesDynamicFormRHFNew';
import VendingShiftReportDynamicForm from './VendingShiftReportDynamicForm';
import type { VendingInventoryCompletedPackRow } from '../../types/vendingInventoryComparisonTypes';
import type { StoreLocation } from '../../types/services/auth';
import UnsupportedReceiptTypeCard from './UnsupportedReceiptTypeCard';

interface FixOcrFormPanelProps {
  reportType: string;
  onRevertToReady: () => void;
  storeName: string;
  editMode: 'form' | 'json' | 'enhanced';
  formData: any;
  apiData: any;
  formKey: string | number;
  formRef: React.RefObject<any>;
  queueId?: string;
  gamesMapState: Map<string, number> | null;
  auditStatus: string;
  saving: boolean;
  savingAction: 'reject' | 'revert' | 'save' | null;
  onSubmitClickHandler: (data: any) => void;
  onFormDataUpdate: (data: any) => void;
  onSwitchModes: (data: any, mode: 'form' | 'json') => void;
  handleJsonOnChange: (data: any) => void;
  hoveredRowIndex: number | null;
  onRowHover: (index: number | null) => void;
  onGuideLineElementHover?: (element: HTMLElement | null) => void;
  onGuideGridOriginElementChange?: (element: HTMLElement | null) => void;
  varianceByGameId?: AuditVarianceByGameId;
  onRefreshSimulation?: () => void;
  simulationLoading?: boolean;
  simulationError?: string | null;
  vendingInventoryNewPackKeys?: string[];
  vendingInventoryCompletedPacks?: VendingInventoryCompletedPackRow[];
  storeLocations?: StoreLocation[];
}

const FixOcrFormPanel: React.FC<FixOcrFormPanelProps> = ({
  reportType,
  storeName,
  editMode,
  formData,
  apiData,
  formKey,
  formRef,
  queueId,
  gamesMapState,
  auditStatus,
  saving,
  savingAction,
  onSubmitClickHandler,
  onFormDataUpdate,
  onSwitchModes,
  handleJsonOnChange,
  hoveredRowIndex,
  onRowHover,
  onGuideLineElementHover,
  onGuideGridOriginElementChange,
  onRevertToReady,
  varianceByGameId,
  onRefreshSimulation,
  simulationLoading,
  simulationError,
  vendingInventoryNewPackKeys,
  vendingInventoryCompletedPacks,
  storeLocations,
}) => {
  const supportedReportTypes = ['ticket_balance', 'vending_sales', 'vending_inventory', 'vending_shift_report'] as const;
  const isSupportedReportType = supportedReportTypes.includes(reportType as any);
  const normalizedAuditStatus = String(auditStatus || '').toLowerCase();
  const isLocked = normalizedAuditStatus === 'finalized' || normalizedAuditStatus === 'auto_finalized';

  if (!isSupportedReportType) {
    const dataToReject = (formData || apiData || {}) as any;
    return (
      <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <UnsupportedReceiptTypeCard
          receiptType={String(reportType || 'unknown')}
          rejecting={saving || isLocked}
          onReject={() => onSubmitClickHandler({ ...dataToReject, action: 'reject' })}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {formData && editMode === 'form' && reportType === 'ticket_balance' && gamesMapState && (
        <AuditDynamicFormRHFNew
          ref={formRef}
          key={formKey}
          storeName={storeName}
          initialValues={formData}
          onSubmit={onSubmitClickHandler}
          isSubmitting={saving}
          savingAction={savingAction}
          auditStatus={auditStatus}
          ticketDetails={formData?.ticket_details || []}
          onFormDataUpdate={onFormDataUpdate}
          onSwitchModes={onSwitchModes}
          gamesData={gamesMapState}
          varianceByGameId={varianceByGameId}
          onRefreshSimulation={onRefreshSimulation}
          simulationLoading={simulationLoading}
          simulationError={simulationError}
          hoveredRowIndex={hoveredRowIndex}
          onRowHover={onRowHover}
          onRevertToReady={onRevertToReady}
        />
      )}

      {formData && editMode === 'form' && (reportType === 'vending_sales' || reportType === 'vending_inventory') && (
        <>
          <VendingSalesDynamicFormRHFNew
            ref={formRef}
            key={formKey}
            storeName={storeName}
            reportType={reportType}
            queueId={queueId}
            initialValues={formData}
            onSubmit={onSubmitClickHandler}
            isSubmitting={saving}
            savingAction={savingAction}
            auditStatus={auditStatus}
            onFormDataUpdate={onFormDataUpdate}
            onSwitchModes={onSwitchModes}
            gamesData={gamesMapState}
            hoveredRowIndex={hoveredRowIndex}
            onRowHover={onRowHover}
            onRevertToReady={onRevertToReady}
            vendingInventoryNewPackKeys={vendingInventoryNewPackKeys}
            vendingInventoryCompletedPacks={vendingInventoryCompletedPacks}
            storeLocations={storeLocations}
          />
        </>
      )}

      {formData && editMode === 'form' && reportType === 'vending_shift_report' && (
        <VendingShiftReportDynamicForm
          ref={formRef}
          key={formKey}
          storeName={storeName}
          initialValues={formData}
          onSubmit={onSubmitClickHandler}
          isSubmitting={saving}
          savingAction={savingAction}
          auditStatus={auditStatus}
          queueId={queueId}
          onFormDataUpdate={onFormDataUpdate}
          onSwitchModes={onSwitchModes}
          onRevertToReady={onRevertToReady}
          storeLocations={storeLocations}
          gamesData={gamesMapState}
          hoveredRowIndex={hoveredRowIndex}
          onRowHover={onRowHover}
          onGuideLineElementHover={onGuideLineElementHover}
          onGuideGridOriginElementChange={onGuideGridOriginElementChange}
          vendingInventoryNewPackKeys={vendingInventoryNewPackKeys}
          vendingInventoryCompletedPacks={vendingInventoryCompletedPacks}
        />
      )}

      {apiData && editMode === 'json' && (
        <>
          <Box sx={{ height: '1300px', maxHeight: '1300px', overflow: 'auto' }}>
            <SimpleAuditForm
              initialData={apiData}
              onChange={handleJsonOnChange}
              onValidationChange={() => { }}
              onSwitchModes={onSwitchModes}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onSubmitClickHandler(apiData)}
            disabled={saving || isLocked}
            fullWidth
            sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', backgroundColor: '#2C6CA0', mt: 2 }}
          >
            {savingAction === 'save' || savingAction === 'reject'
              ? 'Saving...'
              : (apiData?.action === 'reject' ? 'Reject' : 'Process')}
          </Button>
        </>
      )}
    </Box>
  );
};

export default FixOcrFormPanel;
